import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAuth } from './AuthContext';
import { SHOP_ITEM_DARK_MODE, THEME_STORAGE_KEY } from '../services/userProfile';

const ThemeContext = createContext({
  theme: 'light',
  darkModeOwned: false,
  setTheme: () => {},
  enableDarkAfterPurchase: () => {},
});

export function useTheme() {
  return useContext(ThemeContext);
}

function readStoredTheme() {
  try {
    const v = localStorage.getItem(THEME_STORAGE_KEY);
    if (v === 'dark' || v === 'light') return v;
  } catch {
    /* ignore */
  }
  return null;
}

function writeStoredTheme(theme) {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    /* ignore */
  }
}

/** Keep dark off <html> so Partner / Game never inherit via the document. */
function clearDocumentTheme() {
  if (typeof document === 'undefined') return;
  document.documentElement.removeAttribute('data-theme');
}

/**
 * Theme preference for shop Dark Mode unlock.
 * Visual tokens apply on `.public-page[data-theme="dark"]` and `.admin-hub[data-theme="dark"]`
 * (not Game, not `<html>`).
 */
export function ThemeProvider({ children }) {
  const { userProfile } = useAuth();
  const darkModeOwned = Boolean(userProfile?.shopOwned?.[SHOP_ITEM_DARK_MODE]);
  const [theme, setThemeState] = useState(() => readStoredTheme() || 'light');

  useEffect(() => {
    clearDocumentTheme();
    if (!darkModeOwned) {
      setThemeState('light');
      return;
    }
    const stored = readStoredTheme();
    const next = stored || 'light';
    setThemeState(next);
  }, [darkModeOwned]);

  const setTheme = useCallback(
    (next) => {
      if (!darkModeOwned) return;
      const value = next === 'dark' ? 'dark' : 'light';
      writeStoredTheme(value);
      setThemeState(value);
      clearDocumentTheme();
    },
    [darkModeOwned]
  );

  /** Unlock only — do not auto-flip paper pages to dark after purchase. */
  const enableDarkAfterPurchase = useCallback(() => {
    writeStoredTheme('light');
    setThemeState('light');
    clearDocumentTheme();
  }, []);

  const value = useMemo(
    () => ({
      theme: darkModeOwned ? theme : 'light',
      darkModeOwned,
      setTheme,
      enableDarkAfterPurchase,
    }),
    [theme, darkModeOwned, setTheme, enableDarkAfterPurchase]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
